import {
    Appointment, AppointmentCategory,
    AppointmentInvitation,
    AppointmentTypes, InvitationAppointment, InvitationStatus, ParticipationType, Specialization, Week, EventStatus
} from "@web-libs/features/appointments/domain/appointment.types";
import {dateStringToUTCDate} from "@web-libs/util/dates/date";
import {parseUserDTO, UserDTO} from '@web-libs/features/users/api/user-dto.types';
import {TrmzFileDTOConverter} from '@web-libs/features/files/trmz-file/api/trmz-file-dto-converter';


export type AppointmentDTO = {
    id: string
    name: string
    startDate: string
    endDate: string
    description?: string
    fixedEvent?: boolean
    bookableEvent?: boolean,
    invitationEvent?: boolean,
    invitationStatus?: InvitationStatus
    signOnAllowed?: boolean,
    signOffAllowed?: boolean,
    videoMeeting?: {name: string, id: string},
    organizer?: UserDTO,
    maxParticipants: number,
    participationTypes?: ParticipationType[],
    enableParticipationInfo?: boolean,
    categories?: AppointmentCategoryDTO[],
    status?: EventStatus
    canBeReassigned: boolean,
    billable: boolean,
    cancelled?: boolean
}

export type  WeekDTO = {
    startDate: Date;
    endDate: Date;
}


export type SpecializationDTO = {
    id: string,
    name: string,
}


export type AppointmentCategoryDTO = {
    id: string
    name: string
    description?: string
}

export const parseAppointmentDTO = (appointmentDTO: AppointmentDTO, converter: TrmzFileDTOConverter, appointmentType?: AppointmentTypes): Appointment => {
    const appointment: Appointment = {
        id: appointmentDTO.id,
        type: appointmentType ?? setAppointmentType(appointmentDTO),
        name: appointmentDTO.name,
        startDate: dateStringToUTCDate(appointmentDTO.startDate),
        endDate: appointmentDTO.endDate ? dateStringToUTCDate(appointmentDTO.endDate) : undefined,
        description: appointmentDTO.description,
        signOnAllowed: appointmentDTO.signOnAllowed,
        signOffAllowed: appointmentDTO.signOffAllowed,
        videoMeeting: appointmentDTO.videoMeeting? {name: appointmentDTO.videoMeeting.name, id: appointmentDTO.videoMeeting.id} : undefined,
        organizer: appointmentDTO.organizer ? parseUserDTO(appointmentDTO.organizer, converter) : undefined,
        maxParticipants: appointmentDTO.maxParticipants,
        participationTypes: appointmentDTO.participationTypes,
        enableParticipationInfo: appointmentDTO.enableParticipationInfo,
        categories: appointmentDTO.categories ? parseAppointmenCategoriesDTO(appointmentDTO.categories) : [],
        status: appointmentDTO.status,
        canBeReassigned: !!appointmentDTO.canBeReassigned,
        billable: !!appointmentDTO.billable,
        cancelled: !!appointmentDTO.cancelled
    }
    if (appointment.type == "InvitationAppointment") {
      return {...appointment, invitationStatus: parseInvitationStatus(appointmentDTO.invitationStatus!)} as InvitationAppointment
    }
    return appointment
}

export const parseInvitationStatus = (invitationStatusString: string): InvitationStatus => {
    if (invitationStatusString == "ACCEPTED") return 'Accepted'
    if (invitationStatusString == "DECLINED") return 'Declined'
    return 'Pending'
}

export const setAppointmentType = (appointmentDTO: AppointmentDTO): AppointmentTypes => {
    if (appointmentDTO.bookableEvent) return "BookableAppointment"
    if (appointmentDTO.invitationEvent) return "InvitationAppointment"
    return "FixedAppointment"
}

export const parseAppointmentsDTO = (dtos: AppointmentDTO[], converter: TrmzFileDTOConverter, appointmentType?: AppointmentTypes): Appointment[] => {
    return dtos.map((appointmentDTO: AppointmentDTO) => parseAppointmentDTO(appointmentDTO, converter, appointmentType));
}

export const parseWeekDTO = (weekDTO: WeekDTO): Week => {
    return {
        startDate: new Date(weekDTO.startDate),
        endDate: new Date(weekDTO.endDate),
    }
}

export const parseAppointmentDTOsTOInvitations = (appointmentsDTO: AppointmentDTO[], converter: TrmzFileDTOConverter): AppointmentInvitation[] => {
    return  appointmentsDTO.map((appointmentDTO) => {
        return {
            appointment: {
                ...parseAppointmentDTO(appointmentDTO, converter,"InvitationAppointment"),
                type: "InvitationAppointment",
                invitationStatus: "Pending"
            }
        };
    })
}

export const parseSpecializationDTO = (specializationDTO: SpecializationDTO): Specialization => {
    return {
        id: specializationDTO.id,
        name: specializationDTO.name
    }
}

export const parseSpecializationsDTO = (specializationsDTO: SpecializationDTO[]): Specialization[] =>
    specializationsDTO.map((specializationDTO: SpecializationDTO) => parseSpecializationDTO(specializationDTO))

export const parseAppointmentCategoryDTO = (appointmentCategoryDTO: AppointmentCategoryDTO): AppointmentCategory => {
    return {
        id: appointmentCategoryDTO.id,
        name: appointmentCategoryDTO.name,
        description: appointmentCategoryDTO.description,
    }
}

export const parseAppointmenCategoriesDTO = (appointmentsDTO: AppointmentCategoryDTO[]): AppointmentCategory[] =>
    appointmentsDTO.map(
        (appointmentCategoryDTO) => parseAppointmentCategoryDTO(appointmentCategoryDTO)
    )
